import { useEffect, useRef, useState } from 'react';
import { adminNavItems } from '../admin/adminNav';

export const ASSIGN_USERS_PATH = '/admin/assign-users';
export const ASSIGN_USERS_NEW_PATH = '/admin/assign-users/new';

export function assignUserEditPath(userId) {
  return `/admin/assign-users/${userId}/edit`;
}

export const EMPTY_USER_FORM = {
  name: '',
  email: '',
  password: '',
  phone: '',
  modules: ['home'],
  isActive: true,
};

export function moduleLabel(moduleId) {
  return adminNavItems.find((item) => item.id === moduleId)?.label || moduleId;
}

function getUserInitials(name) {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function StatCard({ label, value, tone = 'default' }) {
  const isGood = tone === 'good';
  const isMuted = tone === 'muted';

  const dotColor = isGood ? 'bg-emerald-500' : isMuted ? 'bg-slate-400' : 'bg-[rgb(var(--crm-accent))]';
  const valColor = isGood
    ? 'text-emerald-600 dark:text-emerald-400'
    : isMuted
      ? 'text-[rgb(var(--crm-text-muted))]'
      : 'text-[rgb(var(--crm-text))]';

  const subtext = isGood ? 'Can sign in & access' : isMuted ? 'Access suspended' : 'Staff logins';

  return (
    <div className="rounded-xl border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] p-4 shadow-xs transition hover:border-[rgb(var(--crm-accent)/0.3)]">
      <div className="flex items-center gap-1.5">
        <span className={`h-2 w-2 rounded-full ${dotColor} ${isGood ? 'animate-pulse' : ''}`} />
        <span className="text-[11px] font-bold uppercase tracking-wider text-[rgb(var(--crm-text-muted))]">
          {label}
        </span>
      </div>
      <p className={`mt-2 text-2xl font-extrabold tracking-tight ${valColor} tabular-nums`}>
        {value}
      </p>
      <p className="mt-1 text-[11px] text-[rgb(var(--crm-text-muted))]">{subtext}</p>
    </div>
  );
}

function UserActionMenu({ user, onEdit, onToggleActive, onResendInvite }) {
  const [open, setOpen] = useState(false);
  const [openUpwards, setOpenUpwards] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const toggleOpen = () => {
    if (!open && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const container = menuRef.current.closest('.overflow-x-auto');
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const spaceBelowInContainer = containerRect.bottom - rect.bottom;
        if (spaceBelowInContainer < 140) {
          setOpenUpwards(true);
        } else {
          setOpenUpwards(false);
        }
      } else {
        const spaceBelow = window.innerHeight - rect.bottom;
        setOpenUpwards(spaceBelow < 160 && rect.top > 160);
      }
    }
    setOpen((prev) => !prev);
  };

  return (
    <div className="relative inline-block text-left" ref={menuRef}>
      <button
        type="button"
        className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border transition shadow-2xs focus:outline-none ${
          open
            ? 'border-[rgb(var(--crm-accent))] bg-[rgb(var(--crm-accent-soft))] text-[rgb(var(--crm-accent))]'
            : 'border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] text-[rgb(var(--crm-text-muted))] hover:bg-[rgb(var(--crm-page-bg))] hover:text-[rgb(var(--crm-text))] hover:border-[rgb(var(--crm-accent)/0.3)]'
        }`}
        onClick={toggleOpen}
        title="More actions"
        aria-label="More actions"
        aria-expanded={open}
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="1.75" />
          <circle cx="12" cy="12" r="1.75" />
          <circle cx="12" cy="19" r="1.75" />
        </svg>
      </button>

      {open && (
        <div
          className={`absolute right-0 z-30 w-44 rounded-xl border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] p-1 shadow-lg ring-1 ring-black/5 animate-in fade-in zoom-in-95 duration-100 ${
            openUpwards ? 'bottom-full mb-1.5 origin-bottom-right' : 'top-full mt-1.5 origin-top-right'
          }`}
        >
          {/* Edit */}
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-[rgb(var(--crm-text))] transition hover:bg-[rgb(var(--crm-accent-soft))] hover:text-[rgb(var(--crm-accent))]"
            onClick={() => {
              setOpen(false);
              onEdit?.(user);
            }}
          >
            <svg className="h-3.5 w-3.5 text-[rgb(var(--crm-text-muted))]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            <span>Edit</span>
          </button>

          {/* Resend invite (if pending) */}
          {user.mustSetPassword && typeof onResendInvite === 'function' ? (
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-amber-700 dark:text-amber-400 transition hover:bg-amber-50 dark:hover:bg-amber-950/40"
              onClick={() => {
                setOpen(false);
                onResendInvite(user);
              }}
            >
              <svg className="h-3.5 w-3.5 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              <span>Resend invite</span>
            </button>
          ) : null}

          <div className="my-1 border-t border-[rgb(var(--crm-border))]" />

          {/* Activate / Deactivate */}
          <button
            type="button"
            className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition ${
              user.isActive
                ? 'text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40'
                : 'text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40'
            }`}
            onClick={() => {
              setOpen(false);
              onToggleActive?.(user);
            }}
          >
            {user.isActive ? (
              <>
                <svg className="h-3.5 w-3.5 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                </svg>
                <span>Deactivate</span>
              </>
            ) : (
              <>
                <svg className="h-3.5 w-3.5 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span>Activate</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export function UserTable({
  users,
  loading,
  emptyLabel,
  onEdit,
  onToggleActive,
  onResendInvite,
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 px-5 py-10 text-xs text-[rgb(var(--crm-text-muted))]">
        <svg className="h-4 w-4 animate-spin text-[rgb(var(--crm-accent))]" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        <span>Loading users…</span>
      </div>
    );
  }

  if (!users.length) {
    return (
      <div className="px-5 py-10 text-center text-xs text-[rgb(var(--crm-text-muted))]">
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead className="border-b border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-table-head))] text-[11px] font-bold uppercase tracking-wider text-[rgb(var(--crm-text-muted))]">
          <tr>
            <th className="px-5 py-3">Name</th>
            <th className="px-5 py-3">Login</th>
            <th className="px-5 py-3">Sections</th>
            <th className="px-5 py-3">Status</th>
            <th className="px-5 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[rgb(var(--crm-border))]">
          {users.map((user) => (
            <tr
              key={user.id}
              className="hover:bg-[rgb(var(--crm-accent-soft)/0.25)] transition-colors"
            >
              <td className="px-5 py-3.5">
                <div className="flex items-center gap-2.5">
                  <div className="inline-grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[rgb(var(--crm-accent-soft))] text-[11px] font-bold text-[rgb(var(--crm-accent))] shadow-2xs">
                    {getUserInitials(user.name)}
                  </div>
                  <div className="min-w-0">
                    <span className="block font-semibold text-[rgb(var(--crm-text))] truncate">
                      {user.name}
                    </span>
                  </div>
                </div>
              </td>

              <td className="px-5 py-3.5">
                <span className="text-xs font-medium text-[rgb(var(--crm-text-muted))] font-mono">
                  {user.email}
                </span>
              </td>

              <td className="px-5 py-3.5">
                <div className="flex flex-wrap gap-1 max-w-xs">
                  {(user.modules || []).map((mId) => (
                    <span
                      key={mId}
                      className="inline-flex items-center rounded-md border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))] px-2 py-0.5 text-[11px] font-medium text-[rgb(var(--crm-text))]"
                    >
                      {moduleLabel(mId)}
                    </span>
                  ))}
                  {!user.modules?.length && (
                    <span className="text-xs text-[rgb(var(--crm-text-muted))]">None</span>
                  )}
                </div>
              </td>

              <td className="px-5 py-3.5">
                <div className="flex flex-col gap-1">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[rgb(var(--crm-text))]">
                    <span
                      className={`h-2 w-2 rounded-full ${
                        user.isActive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'
                      }`}
                    />
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {user.mustSetPassword ? (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                      Invite pending
                    </span>
                  ) : null}
                </div>
              </td>

              <td className="px-5 py-3.5 text-right">
                <UserActionMenu
                  user={user}
                  onEdit={onEdit}
                  onToggleActive={onToggleActive}
                  onResendInvite={onResendInvite}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
